import React, { useEffect, useState, useCallback, useRef } from 'react';
import { CheckCircle2, XCircle, Info, X, AlertTriangle } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info';

export interface ToastMessage {
    id: string;
    message: string;
    type: ToastType;
}

export interface ConfirmOptions {
    title?: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    tone?: 'default' | 'danger';
    dismissible?: boolean;
    showCloseButton?: boolean;
    inputLabel?: string;
    inputPlaceholder?: string;
    inputDefaultValue?: string;
    inputRequired?: boolean;
    inputMaxLength?: number;
}

export type ConfirmAction = 'confirm' | 'cancel' | 'dismiss';

export interface ConfirmResult {
    action: ConfirmAction;
    value?: string;
}

interface ToastProps {
    message: string;
    type: ToastType;
    onClose: () => void;
    duration?: number;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose, duration = 3000 }) => {
    useEffect(() => {
        const timer = setTimeout(() => {
            onClose();
        }, duration);
        return () => clearTimeout(timer);
    }, [onClose, duration]);

    const icons = {
        success: <CheckCircle2 className="w-5 h-5 text-emerald-600" />,
        error: <XCircle className="w-5 h-5 text-red-600" />,
        info: <Info className="w-5 h-5 text-blue-600" />
    };

    const styles = {
        success: 'bg-emerald-50 border-emerald-200',
        error: 'bg-red-50 border-red-200',
        info: 'bg-blue-50 border-blue-200'
    };

    const textColors = {
        success: 'text-emerald-800',
        error: 'text-red-800',
        info: 'text-blue-800'
    };

    return (
        <div
            className={`
                ${styles[type]} 
                border rounded-2xl shadow-xl px-5 py-4 
                flex items-center gap-3 min-w-[300px] max-w-[400px]
                animate-toast-slide-down backdrop-blur-sm
            `}
        >
            <div className="flex-shrink-0">
                {icons[type]}
            </div>
            <p className={`text-sm font-medium flex-1 ${textColors[type]}`}>
                {message}
            </p>
            <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
            >
                <X className="w-4 h-4" />
            </button>
        </div>
    );
};

// Toast Container Component for managing multiple toasts
interface ToastContainerProps {
    toasts: ToastMessage[];
    onRemove: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onRemove }) => {
    if (toasts.length === 0) return null;

    return (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[9999] flex flex-col gap-3">
            {toasts.map((toast) => (
                <Toast
                    key={toast.id}
                    message={toast.message}
                    type={toast.type}
                    onClose={() => onRemove(toast.id)}
                />
            ))}
        </div>
    );
};

// Event Bus Constant
const TOAST_EVENT = 'valkyrie-show-toast';
const CONFIRM_EVENT = 'valkyrie-show-confirm';
let activeToastWrapperOwner: symbol | null = null;
let activeConfirmWrapperOwner: symbol | null = null;

// Global Wrapper Component
export const ToastWrapper: React.FC = () => {
    const ownerRef = useRef<symbol | null>(null);
    const [isPrimary] = useState(() => {
        const owner = Symbol('toast-wrapper');
        ownerRef.current = owner;
        if (!activeToastWrapperOwner) {
            activeToastWrapperOwner = owner;
            return true;
        }
        return activeToastWrapperOwner === owner;
    });
    const [toasts, setToasts] = useState<ToastMessage[]>([]);

    useEffect(() => {
        if (!isPrimary) return;

        const handleToast = (e: Event) => {
            const detail = (e as CustomEvent).detail;
            const id = Date.now().toString() + Math.random().toString().slice(2);
            setToasts(prev => [...prev, { id, message: detail.message, type: detail.type }]);
        };

        window.addEventListener(TOAST_EVENT, handleToast);
        return () => {
            window.removeEventListener(TOAST_EVENT, handleToast);
            if (activeToastWrapperOwner === ownerRef.current) {
                activeToastWrapperOwner = null;
            }
        };
    }, [isPrimary]);

    if (!isPrimary) return null;

    const removeToast = (id: string) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    };

    return <ToastContainer toasts={toasts} onRemove={removeToast} />;
};

// Hook for easy toast management
export const useToast = () => {
    const showToast = useCallback((message: string, type: ToastType = 'info') => {
        const event = new CustomEvent(TOAST_EVENT, { detail: { message, type } });
        window.dispatchEvent(event);
    }, []);

    // Return the global wrapper component
    return { showToast, ToastWrapper };
};

// Confirm Dialog (Toast-styled) with global event bus
interface ConfirmPayload extends ConfirmOptions {
    id: string;
    resolve: (value: ConfirmResult) => void;
}

const ConfirmDialog: React.FC<{
    payload: ConfirmPayload;
    onConfirm: (value?: string) => void;
    onCancel: () => void;
    onDismiss: () => void;
}> = ({ payload, onConfirm, onCancel, onDismiss }) => {
    const {
        title = '确认操作',
        message,
        confirmText = '确认',
        cancelText = '取消',
        tone = 'default',
        dismissible = true,
        showCloseButton = dismissible,
        inputLabel,
        inputPlaceholder,
        inputDefaultValue = '',
        inputRequired = false,
        inputMaxLength = 500
    } = payload;
    const [inputValue, setInputValue] = useState(inputDefaultValue);

    const isDanger = tone === 'danger';
    const hasInput = Boolean(inputLabel || inputPlaceholder);
    const isConfirmDisabled = hasInput && inputRequired && !inputValue.trim();

    useEffect(() => {
        setInputValue(inputDefaultValue);
    }, [inputDefaultValue, payload.id]);

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center">
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                onClick={dismissible ? onDismiss : undefined}
            />
            <div className="relative w-[92%] max-w-md rounded-2xl border border-gray-100 bg-white shadow-2xl p-6 animate-scale-in">
                <div className="flex items-start gap-3">
                    <div className={`mt-0.5 flex h-10 w-10 items-center justify-center rounded-full ${isDanger ? 'bg-red-50' : 'bg-blue-50'}`}>
                        <AlertTriangle className={`h-5 w-5 ${isDanger ? 'text-red-600' : 'text-blue-600'}`} />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
                        <p className="mt-2 text-sm text-gray-600">{message}</p>
                        {hasInput && (
                            <div className="mt-4">
                                {inputLabel && (
                                    <label className="mb-2 block text-xs font-semibold text-gray-500">
                                        {inputLabel}
                                    </label>
                                )}
                                <textarea
                                    value={inputValue}
                                    onChange={(event) => setInputValue(event.target.value.slice(0, inputMaxLength))}
                                    placeholder={inputPlaceholder}
                                    className="min-h-[104px] w-full resize-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 outline-none transition-colors focus:border-blue-300 focus:bg-white focus:ring-2 focus:ring-blue-50"
                                    maxLength={inputMaxLength}
                                />
                                <div className="mt-1.5 text-right text-[11px] text-gray-400">
                                    {inputValue.length}/{inputMaxLength}
                                </div>
                            </div>
                        )}
                    </div>
                    {showCloseButton && (
                        <button
                            onClick={onDismiss}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            aria-label="取消"
                            title="取消"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>
                <div className="mt-6 flex justify-end gap-3">
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 rounded-full text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                    >
                        {cancelText}
                    </button>
                    <button
                        onClick={() => onConfirm(inputValue)}
                        disabled={isConfirmDisabled}
                        className={`px-4 py-2 rounded-full text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:bg-gray-300 ${isDanger ? 'bg-red-500 hover:bg-red-600' : 'bg-black hover:bg-gray-900'}`}
                    >
                        {confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
};

export const ConfirmWrapper: React.FC = () => {
    const ownerRef = useRef<symbol | null>(null);
    const [isPrimary] = useState(() => {
        const owner = Symbol('confirm-wrapper');
        ownerRef.current = owner;
        if (!activeConfirmWrapperOwner) {
            activeConfirmWrapperOwner = owner;
            return true;
        }
        return activeConfirmWrapperOwner === owner;
    });
    const [payload, setPayload] = useState<ConfirmPayload | null>(null);

    useEffect(() => {
        if (!isPrimary) return;

        const handleConfirm = (e: Event) => {
            const detail = (e as CustomEvent).detail as ConfirmPayload;
            setPayload(detail);
        };
        window.addEventListener(CONFIRM_EVENT, handleConfirm);
        return () => {
            window.removeEventListener(CONFIRM_EVENT, handleConfirm);
            if (activeConfirmWrapperOwner === ownerRef.current) {
                activeConfirmWrapperOwner = null;
            }
        };
    }, [isPrimary]);

    if (!isPrimary) return null;

    const close = (action: ConfirmAction, value?: string) => {
        if (payload) {
            payload.resolve({ action, value });
        }
        setPayload(null);
    };

    if (!payload) return null;
    return (
        <ConfirmDialog
            payload={payload}
            onConfirm={(value) => close('confirm', value)}
            onCancel={() => close('cancel')}
            onDismiss={() => close('dismiss')}
        />
    );
};

export const useConfirm = () => {
    const confirm = useCallback((options: ConfirmOptions | string) => {
        const normalized: ConfirmOptions = typeof options === 'string' ? { message: options } : options;
        return new Promise<ConfirmResult>((resolve) => {
            const event = new CustomEvent(CONFIRM_EVENT, {
                detail: {
                    id: Date.now().toString() + Math.random().toString().slice(2),
                    resolve,
                    ...normalized
                }
            });
            window.dispatchEvent(event);
        });
    }, []);

    return { confirm, ConfirmWrapper };
};

export default Toast;
